import type { ArrayAccess, Declaration, Expression, GlossaProgram, LValue, Statement, SwitchCaseValue } from '../parser/ast.js';

/**
 * Το πλήθος των δεικτών σε ΚΑΘΕ χρήση ενός πίνακα (π.χ. `β[i]` ή `β[i, j]`)
 * πρέπει να ταιριάζει ΑΚΡΙΒΩΣ με το πλήθος των διαστάσεων που δηλώθηκαν στο
 * ΜΕΤΑΒΛΗΤΕΣ (π.χ. `ΑΚΕΡΑΙΕΣ: β[3, 4]` → 2 διαστάσεις). Χωρίς αυτόν τον
 * έλεγχο, μια τέτοια ασυμφωνία σκάει ΜΟΝΟ στο runtime, σαν ένα εντελώς
 * παραπλανητικό "Ο δείκτης [Ν] είναι εκτός ορίων του πίνακα" (bug report: ο
 * χρήστης δήλωσε `β[3,4]` αλλά έγραψε `β[ξ]` — το πραγματικό πρόβλημα είναι
 * το πλήθος των δεικτών, όχι κάποια συγκεκριμένη τιμή εκτός ορίων).
 *
 * Τρέχει ΠΡΙΝ την εκτέλεση (ίδια λογική με `findUndeclaredGlossaReference`/
 * `findArrayParameterSizeMismatch`) ώστε να πιάνει το λάθος ΑΚΟΜΑ ΚΙ αν
 * βρίσκεται σε κλάδο/βρόχο που δεν θα εκτελούνταν σε αυτό το συγκεκριμένο
 * τρέξιμο — "έλεγξε πρώτα όλους τους πίνακες, μετά τρέξε τον αλγόριθμο".
 *
 * Μόνο ΓΛΩΣΣΑ (`GlossaProgram`) — η Ψευδογλώσσα δεν δηλώνει καν διαστάσεις
 * πίνακα εκ των προτέρων.
 */
export interface ArrayDimensionMismatch {
  readonly arrayName: string;
  readonly declaredDimensions: number;
  readonly usedIndices: number;
  readonly line: number;
  readonly column: number;
}

function declaredDimensionCount(declarations: readonly Declaration[], name: string): number | null {
  for (const decl of declarations) {
    if (decl.kind !== 'VariableDeclaration') continue;
    for (const declarator of decl.declarators) {
      if (declarator.name !== name) continue;
      // Σκόπιμα ΜΟΝΟ για πίνακες (>=1 διάσταση) — μια απλή (μη-πίνακα)
      // μεταβλητή που χρησιμοποιείται σαν πίνακας είναι διαφορετικό, ήδη
      // υπαρκτό είδος σφάλματος (βλ. runtime check στο `Interpreter`), όχι
      // "ασυμφωνία πλήθους διαστάσεων" — δεν το αγγίζουμε εδώ.
      return declarator.dimensions.length > 0 ? declarator.dimensions.length : null;
    }
  }
  return null;
}

function dimensionWord(n: number): string {
  return n === 1 ? '1 διάσταση' : `${n} διαστάσεις`;
}

function indexWord(n: number): string {
  return n === 1 ? '1 δείκτη' : `${n} δείκτες`;
}

function checkArrayAccess(access: ArrayAccess, declarations: readonly Declaration[]): ArrayDimensionMismatch | null {
  const declared = declaredDimensionCount(declarations, access.array.name);
  if (declared !== null && declared !== access.indices.length) {
    return {
      arrayName: access.array.name,
      declaredDimensions: declared,
      usedIndices: access.indices.length,
      line: access.array.line,
      column: access.array.column,
    };
  }
  for (const idx of access.indices) {
    const result = checkExpr(idx, declarations);
    if (result !== null) return result;
  }
  return null;
}

function checkExpr(expr: Expression, declarations: readonly Declaration[]): ArrayDimensionMismatch | null {
  switch (expr.kind) {
    case 'NumberLiteral':
    case 'StringLiteral':
    case 'BooleanLiteral':
    case 'Identifier':
      return null;
    case 'ArrayAccess':
      return checkArrayAccess(expr, declarations);
    case 'FunctionCall': {
      for (const arg of expr.args) {
        const result = checkExpr(arg, declarations);
        if (result !== null) return result;
      }
      return null;
    }
    case 'UnaryExpression':
      return checkExpr(expr.operand, declarations);
    case 'BinaryExpression': {
      const left = checkExpr(expr.left, declarations);
      if (left !== null) return left;
      return checkExpr(expr.right, declarations);
    }
  }
}

function checkLValue(lvalue: LValue, declarations: readonly Declaration[]): ArrayDimensionMismatch | null {
  if (lvalue.kind === 'Identifier') return null;
  return checkArrayAccess(lvalue, declarations);
}

function checkSwitchCaseValue(value: SwitchCaseValue, declarations: readonly Declaration[]): ArrayDimensionMismatch | null {
  if (value.kind === 'CaseRange') {
    const from = checkExpr(value.from, declarations);
    if (from !== null) return from;
    return checkExpr(value.to, declarations);
  }
  if (value.kind === 'CaseComparison') return checkExpr(value.value, declarations);
  return checkExpr(value, declarations);
}

function checkStatement(stmt: Statement, declarations: readonly Declaration[]): ArrayDimensionMismatch | null {
  switch (stmt.kind) {
    case 'AssignmentStatement': {
      const value = checkExpr(stmt.value, declarations);
      if (value !== null) return value;
      return checkLValue(stmt.target, declarations);
    }
    case 'ReadStatement': {
      for (const target of stmt.targets) {
        const result = checkLValue(target, declarations);
        if (result !== null) return result;
      }
      return null;
    }
    case 'WriteStatement': {
      for (const value of stmt.values) {
        const result = checkExpr(value, declarations);
        if (result !== null) return result;
      }
      return null;
    }
    case 'SwapStatement': {
      const left = checkLValue(stmt.left, declarations);
      if (left !== null) return left;
      return checkLValue(stmt.right, declarations);
    }
    case 'IfStatement': {
      const condition = checkExpr(stmt.condition, declarations);
      if (condition !== null) return condition;
      const thenResult = checkStatements(stmt.thenBranch, declarations);
      if (thenResult !== null) return thenResult;
      for (const branch of stmt.elseIfBranches) {
        const branchCondition = checkExpr(branch.condition, declarations);
        if (branchCondition !== null) return branchCondition;
        const branchResult = checkStatements(branch.body, declarations);
        if (branchResult !== null) return branchResult;
      }
      if (stmt.elseBranch !== null) return checkStatements(stmt.elseBranch, declarations);
      return null;
    }
    case 'ForStatement': {
      const from = checkExpr(stmt.from, declarations);
      if (from !== null) return from;
      const to = checkExpr(stmt.to, declarations);
      if (to !== null) return to;
      if (stmt.step !== null) {
        const step = checkExpr(stmt.step, declarations);
        if (step !== null) return step;
      }
      return checkStatements(stmt.body, declarations);
    }
    case 'WhileStatement': {
      const condition = checkExpr(stmt.condition, declarations);
      if (condition !== null) return condition;
      return checkStatements(stmt.body, declarations);
    }
    case 'RepeatUntilStatement': {
      const bodyResult = checkStatements(stmt.body, declarations);
      if (bodyResult !== null) return bodyResult;
      return checkExpr(stmt.condition, declarations);
    }
    case 'SwitchStatement': {
      const discriminant = checkExpr(stmt.discriminant, declarations);
      if (discriminant !== null) return discriminant;
      for (const switchCase of stmt.cases) {
        for (const value of switchCase.values) {
          const valueResult = checkSwitchCaseValue(value, declarations);
          if (valueResult !== null) return valueResult;
        }
        const bodyResult = checkStatements(switchCase.body, declarations);
        if (bodyResult !== null) return bodyResult;
      }
      if (stmt.elseBody !== null) return checkStatements(stmt.elseBody, declarations);
      return null;
    }
    case 'CallStatement': {
      for (const arg of stmt.args) {
        const result = checkExpr(arg, declarations);
        if (result !== null) return result;
      }
      return null;
    }
  }
}

function checkStatements(statements: readonly Statement[], declarations: readonly Declaration[]): ArrayDimensionMismatch | null {
  for (const stmt of statements) {
    const result = checkStatement(stmt, declarations);
    if (result !== null) return result;
  }
  return null;
}

/**
 * Επιστρέφει την ΠΡΩΤΗ ασυμφωνία πλήθους διαστάσεων πίνακα (με σειρά
 * εμφάνισης — κύριο πρόγραμμα πρώτα, μετά κάθε υποπρόγραμμα με τη σειρά
 * του), ή `null` αν δεν υπάρχει καμία. Κάθε υποπρόγραμμα ελέγχεται με τις
 * ΔΙΚΕΣ ΤΟΥ δηλώσεις (αυστηρά τοπική εμβέλεια, ίδιο σκεπτικό με
 * `findUndeclaredGlossaReference`).
 */
export function findArrayDimensionMismatch(program: GlossaProgram): ArrayDimensionMismatch | null {
  const mainResult = checkStatements(program.body, program.declarations);
  if (mainResult !== null) return mainResult;

  for (const sub of program.subprograms) {
    const subResult = checkStatements(sub.body, sub.declarations);
    if (subResult !== null) return subResult;
  }
  return null;
}

export { dimensionWord, indexWord };
